/* ============================================================================
   Kawaii Co. footer (sections/kc-footer.liquid): brings in the drawing.

   The shop front's art is ~115KB of SVG, so it isn't in the page. When the
   footer comes within ~800px of the screen this fetches
   kc-footer-scene-desktop.svg (990px and up) or kc-footer-scene-phone.svg,
   parses it and moves its pieces in:
     svg.sf-far, svg.sf-near   behind the links (first in the board)
     svg.sf-front, svg.sf-fx   in front of them (CSS stacks them on top)
     svg.kc-ft-leaf            into the door leaf, under the sign
     svg.kc-ft-guest-art       into the doorway (the mascot)
   If the screen crosses 990px later it swaps in the other drawing.

   Also:
   - pauses every animation while the footer is off screen (.is-idle)
   - opens the door on a tap (touch screens have no hover)
   - pops the social badges onto the glass the first time that window
     scrolls into view (.has-pop, then .is-in on .kc-ft-soc); skipped when
     the visitor prefers reduced motion, so the badges simply sit there
   - marks the newsletter form .is-sending while it posts (the button's
     stitches race), and clears it if the browser brings the page back
   - with Season set to Auto, re-checks the month in the shop's time zone,
     in case the page came from a cache made last month

   The theme editor re-renders the section on every change; the new
   <kc-footer> connects and draws again, the old one cleans up.
   ============================================================================ */
(() => {
  if (customElements.get('kc-footer')) return;

  const DESKTOP = '(min-width: 990px)';

  // One request per file per page view, shared by every re-render.
  const drawings = new Map();

  function loadDrawing(url) {
    if (!drawings.has(url)) {
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`kc-footer: ${response.status} for ${url}`);
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
          if (doc.querySelector('parsererror')) throw new Error(`kc-footer: ${url} is not valid SVG`);
          return doc;
        });
      // let a later attempt try again after a failure
      request.catch(() => drawings.delete(url));
      drawings.set(url, request);
    }
    return drawings.get(url);
  }

  function seasonFor(month) {
    if (month >= 3 && month <= 5) return 'spring';
    if (month >= 6 && month <= 8) return 'summer';
    if (month >= 9 && month <= 11) return 'autumn';
    return 'winter';
  }

  class KcFooter extends HTMLElement {
    connectedCallback() {
      this.comp = this.querySelector('[data-kc-ft-comp]');
      this.scene = this.comp && this.comp.parentElement;
      if (!this.comp || !this.scene) return;

      this.layout = null;
      this.wanted = false;
      this.classList.add('is-waiting');
      this.refreshSeason();

      this.media = window.matchMedia(DESKTOP);
      this.onMedia = () => this.draw();
      this.media.addEventListener('change', this.onMedia);

      // Start loading a screen or so before the footer shows up.
      this.nearby = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.nearby.disconnect();
          this.wanted = true;
          this.draw();
        },
        { rootMargin: '800px 0px' }
      );
      this.nearby.observe(this.scene);

      // Nothing needs to move while nobody can see it.
      this.visible = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.classList.toggle('is-idle', !entry.isIntersecting);
      });
      this.visible.observe(this);

      this.door = this.querySelector('.kc-ft-door');
      this.onDoorTap = (event) => {
        if (event.pointerType === 'mouse') return;
        this.door.classList.toggle('is-open');
      };
      if (this.door) this.door.addEventListener('pointerup', this.onDoorTap);

      const socials = this.querySelector('.kc-ft-soc');
      if (socials && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.add('has-pop');
        // The first report can be a sliver at the screen's edge, so check how much shows.
        this.seen = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.intersectionRatio >= 0.4)) return;
            this.seen.disconnect();
            socials.classList.add('is-in');
          },
          { threshold: 0.4 }
        );
        this.seen.observe(socials);
      }

      // If something stops the post (a captcha closed, say), the stitches stop racing after a while.
      this.form = this.querySelector('.kc-ft-news__form');
      this.onSubmit = () => {
        this.form.classList.add('is-sending');
        clearTimeout(this.sending);
        this.sending = setTimeout(() => this.form.classList.remove('is-sending'), 8000);
      };
      this.onPageShow = () => this.form.classList.remove('is-sending');
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit);
        window.addEventListener('pageshow', this.onPageShow);
      }
    }

    disconnectedCallback() {
      if (this.nearby) this.nearby.disconnect();
      if (this.visible) this.visible.disconnect();
      if (this.seen) this.seen.disconnect();
      if (this.media) this.media.removeEventListener('change', this.onMedia);
      if (this.door) this.door.removeEventListener('pointerup', this.onDoorTap);
      if (this.form) {
        this.form.removeEventListener('submit', this.onSubmit);
        window.removeEventListener('pageshow', this.onPageShow);
      }
      clearTimeout(this.sending);
    }

    refreshSeason() {
      if (!this.hasAttribute('data-season-auto')) return;
      try {
        const month = Number(
          new Intl.DateTimeFormat('en-US', {
            month: 'numeric',
            timeZone: this.dataset.tz || 'America/Chicago'
          }).format(new Date())
        );
        if (month) this.dataset.season = seasonFor(month);
      } catch (error) {
        // an unknown time zone: keep the season Liquid picked
      }
    }

    async draw() {
      if (!this.wanted || !this.isConnected) return;
      const layout = this.media.matches ? 'desktop' : 'phone';
      if (layout === this.layout) return;
      this.layout = layout;

      const url = layout === 'desktop' ? this.dataset.artDesktop : this.dataset.artPhone;
      let doc;
      try {
        doc = await loadDrawing(url);
      } catch (error) {
        // No drawing: the links, form and contact lines still work. Show the door too.
        this.layout = null;
        this.classList.remove('is-waiting');
        console.warn(error);
        return;
      }
      // The screen may have crossed 990px (or the section been replaced) meanwhile.
      if (!this.isConnected || this.layout !== layout) return;

      this.querySelectorAll('[data-kc-ft-art]').forEach((node) => node.remove());

      const take = (className) => {
        const source = Array.from(doc.documentElement.children).find((node) => node.classList.contains(className));
        if (!source) return null;
        const node = document.importNode(source, true);
        node.setAttribute('data-kc-ft-art', '');
        return node;
      };
      const far = take('sf-far');
      const near = take('sf-near');
      const front = take('sf-front');
      const fx = take('sf-fx');
      const leaf = take('kc-ft-leaf');
      const guest = take('kc-ft-guest-art');

      this.comp.prepend(...[far, near].filter(Boolean));
      this.comp.append(...[front, fx].filter(Boolean));
      const leafSlot = this.querySelector('[data-kc-ft-leaf]');
      if (leaf && leafSlot) leafSlot.prepend(leaf);
      const guestSlot = this.querySelector('[data-kc-ft-guest]');
      if (guest && guestSlot) guestSlot.append(guest);

      this.classList.remove('is-waiting');
      this.classList.add('is-drawn');
    }
  }

  customElements.define('kc-footer', KcFooter);
})();
